import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  UserFeedback,
  FeedbackType,
  FeedbackSentiment,
  FeedbackContext,
  AdaptiveFeedbackImpact,
} from '../types/feedback';

const ENTRIES_SUBCOLLECTION = 'entries';
const FEEDBACK_COLLECTION = 'feedback';

function newFeedbackId(): string {
  return `fb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function classifyAdaptiveImpact(
  feedbackType: FeedbackType,
  sentiment: FeedbackSentiment,
  message: string,
): AdaptiveFeedbackImpact | null {
  const lower = message.toLowerCase();

  if (feedbackType === 'profile_accuracy' && sentiment === 'negative') {
    return {
      affectedDimensions: ['contentFormat', 'learningApproach', 'depthTolerance', 'sequencing', 'infoDensity', 'motivation', 'viewpointRange'],
      signalDirection: 'reset',
      signalWeight: 0.8,
      processed: false,
      processedAt: null,
    };
  }

  if (feedbackType === 'difficulty') {
    const tooHard = /too hard|too difficult|confusing|lost|overwhelm/.test(lower);
    const tooEasy = /too easy|too simple|boring|basic|trivial/.test(lower);
    if (tooHard) {
      return {
        affectedDimensions: ['depthTolerance', 'infoDensity'],
        signalDirection: 'decrease',
        signalWeight: 0.8,
        processed: false,
        processedAt: null,
      };
    }
    if (tooEasy) {
      return {
        affectedDimensions: ['depthTolerance'],
        signalDirection: 'increase',
        signalWeight: 0.8,
        processed: false,
        processedAt: null,
      };
    }
  }

  if (feedbackType === 'format_preference') {
    return {
      affectedDimensions: ['contentFormat'],
      signalDirection: sentiment === 'negative' ? 'decrease' : 'increase',
      signalWeight: 0.8,
      processed: false,
      processedAt: null,
    };
  }

  return null;
}

export interface SubmitFeedbackParams {
  userId: string;
  feedbackType: FeedbackType;
  message: string;
  sentiment: FeedbackSentiment;
  context: FeedbackContext;
}

export async function submitFeedback(params: SubmitFeedbackParams): Promise<UserFeedback> {
  const feedbackId = newFeedbackId();
  const adaptiveImpact = classifyAdaptiveImpact(
    params.feedbackType,
    params.sentiment,
    params.message,
  );

  const entry: UserFeedback = {
    feedbackId,
    userId: params.userId,
    createdAt: new Date().toISOString(),
    feedbackType: params.feedbackType,
    context: params.context,
    message: params.message,
    sentiment: params.sentiment,
    resolved: false,
    resolvedAt: null,
    adaptiveImpact,
  };

  await setDoc(
    doc(db, FEEDBACK_COLLECTION, params.userId, ENTRIES_SUBCOLLECTION, feedbackId),
    { ...entry, _firestoreCreatedAt: serverTimestamp() },
  );

  return entry;
}

export async function getUserFeedback(userId: string, limit = 50): Promise<UserFeedback[]> {
  const q = query(
    collection(db, FEEDBACK_COLLECTION, userId, ENTRIES_SUBCOLLECTION),
    orderBy('createdAt', 'desc'),
    firestoreLimit(limit),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => d.data() as UserFeedback);
}

export async function getUnprocessedFeedback(userId: string): Promise<UserFeedback[]> {
  const q = query(
    collection(db, FEEDBACK_COLLECTION, userId, ENTRIES_SUBCOLLECTION),
    where('adaptiveImpact.processed', '==', false),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((d) => d.data() as UserFeedback)
    .filter((f) => f.adaptiveImpact !== null);
}

export async function markFeedbackProcessed(
  userId: string,
  feedbackId: string,
  impact: AdaptiveFeedbackImpact,
): Promise<void> {
  const ref = doc(db, FEEDBACK_COLLECTION, userId, ENTRIES_SUBCOLLECTION, feedbackId);
  await updateDoc(ref, {
    adaptiveImpact: {
      ...impact,
      processed: true,
      processedAt: new Date().toISOString(),
    },
  });
}

export async function getFeedbackById(userId: string, feedbackId: string): Promise<UserFeedback | null> {
  const snap = await getDoc(
    doc(db, FEEDBACK_COLLECTION, userId, ENTRIES_SUBCOLLECTION, feedbackId),
  );
  if (!snap.exists()) return null;
  return snap.data() as UserFeedback;
}
